import { AnnotationType, isAnnotationType, isTimeBoxAnnotation } from "@perceptron/core/types";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { isNumber, isString } from "lodash-es";

import { useStreamSource } from "@demo/contexts/StreamSourceContext";
import { isVideoAnnotation, useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import {
  formatAnnotationLabel,
  formatLabelByCounter,
  getAnnotationKey,
  getAnnotationDomId,
} from "@demo/utils/annotations";

export interface PointAnnotationTextProps {
  children?: React.ReactNode;
}

// Keep in sync with SelectOutputFormat/Content.tsx + TrackLabelPane.
const TYPE_ICON: Partial<Record<AnnotationType, IconName>> = {
  [AnnotationType.POINT]: "crosshair",
  [AnnotationType.BOX]: "box-icon",
  [AnnotationType.POLYGON]: "polygon",
};

function PointAnnotationText({ children, ...props }: PointAnnotationTextProps & { [key: string]: unknown }) {
  const source = useStreamSource();
  const isStreaming = props["data-streaming"] === "true";
  const { isKeyHighlighted, isKeyClicked, handleMouseEnter, handleMouseLeave, handleClick, findAnnotationById } =
    useAnnotationInteractions();

  const number = props.number;
  const mention = props.mention;
  const rawAnnotationType = props["data-annotation-type"];
  const annotationType = isAnnotationType(rawAnnotationType) ? rawAnnotationType : AnnotationType.POINT;

  // Generate composite annotation key using source
  const annotationKey = isNumber(number) ? getAnnotationKey(number, source) : "";
  const annotation = findAnnotationById(annotationKey);
  const isVideoControl = !!annotation && isTimeBoxAnnotation(annotation);
  const domId = isNumber(number) ? `point-annotation-${getAnnotationDomId(number, source)}` : "";

  function getTextStyle() {
    if (
      !isVideoAnnotation(annotation) &&
      annotationKey &&
      (isKeyHighlighted(annotationKey) || isKeyClicked(annotationKey))
    ) {
      return "bg-[#F58210] shadow-[0_0_8px_rgba(245,130,16,0.8)]";
    }
    return "bg-[#F58210]/20";
  }

  const labelNumber = props["data-label-number"];
  const label = isString(mention)
    ? mention
    : isNumber(labelNumber)
      ? formatLabelByCounter(annotationType, labelNumber)
      : formatAnnotationLabel(annotationType, annotationKey);

  // If no valid number (invalid coordinates):
  // - If still streaming, render nothing (tag incomplete)
  // - If done streaming, render the original tag as plaintext (tag complete but invalid)
  if (!isNumber(number)) {
    if (isStreaming) {
      return null;
    }
    // Get tag name and convert kebab-case back to snake_case for display
    const rawTagName = props["data-tag-name"];
    const tagName = isString(rawTagName) ? rawTagName.replace(/-/g, "_") : "point";
    const mentionAttr = isString(mention) ? ` mention="${mention}"` : "";
    return (
      <>
        {`<${tagName}${mentionAttr}>`}
        {children}
        {`</${tagName}>`}
      </>
    );
  }

  return (
    <span
      aria-label={isVideoControl ? label : undefined}
      className={cn(
        "relative inline-block max-w-full animate-fade-in cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap align-bottom text-white transition-colors duration-200",
        "rounded-sm border px-1",
        getTextStyle(),
        {
          "annotation-marker-streaming border-[#A95A0B]/60": isStreaming,
          "border-[#A95A0B]": !isStreaming,
        },
        "py-0.5 label-2",
      )}
      data-open="false"
      data-video-annotation-control={isVideoControl || undefined}
      id={domId}
      role={isVideoControl ? "button" : undefined}
      tabIndex={isVideoControl ? 0 : undefined}
      onClick={(event) => {
        if (isVideoControl) event.stopPropagation();
        handleClick(annotationKey, source);
      }}
      onKeyDown={
        isVideoControl
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                handleClick(annotationKey, source);
              }
            }
          : undefined
      }
      onMouseEnter={() => handleMouseEnter([annotationKey], source)}
      onMouseLeave={() => handleMouseLeave(annotationKey)}
      {...props}
    >
      {TYPE_ICON[annotationType] && (
        <Icon className="text-middle mb-0.5 mr-1 inline-block size-4 align-middle" name={TYPE_ICON[annotationType]!} />
      )}
      {label}
    </span>
  );
}

export default PointAnnotationText;
